/**
 * RevenueLedgersPage — Issue #134
 *
 * 收益台账管理页面：
 *   - 列表展示（按 orderProjectId 过滤）
 *   - 解锁收益（项目完成交付后 pending_unlock -> unlocked）
 *   - 核销收益（财务发放后 unlocked -> paid_off）
 *
 * 状态流：待解锁 -> 已解锁 -> 已支付核销
 * 权限门控：REVENUE_LEDGER_READ / UNLOCK / PAY_OFF
 */

import { useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import Decimal from 'decimal.js';
import {
  FINANCE_PERMISSIONS,
  hasPermission,
} from '@/lib/financePermissions';
import {
  useRevenueLedgers,
  type RevenueLedgerResponseDto,
} from '@/hooks/useFinance';

/** Format a Decimal/string amount as CNY with 2 decimal places. */
function fmtCny(amount: string): string {
  return `¥${new Decimal(amount).toFixed(2)}`;
}

const STATUS_LABELS: Record<string, string> = {
  pending_unlock: '待解锁',
  unlocked: '已解锁',
  paid_off: '已支付核销',
};

const REVENUE_TYPE_LABELS: Record<string, string> = {
  service: '服务',
  lab: '实验室',
  consumable: '耗材',
  other: '其他',
};

function StatusBadge({ status }: { status: string }) {
  const colorMap: Record<string, string> = {
    pending_unlock: 'bg-yellow-100 text-yellow-800',
    unlocked: 'bg-blue-100 text-blue-800',
    paid_off: 'bg-green-100 text-green-800',
  };
  const cls = colorMap[status] ?? 'bg-gray-100 text-gray-600';
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>
      {STATUS_LABELS[status] ?? status}
    </span>
  );
}

export default function RevenueLedgersPage() {
  const { permissions } = useAuth();
  const canRead = hasPermission(permissions, FINANCE_PERMISSIONS.REVENUE_LEDGER_READ);
  const canUnlock = hasPermission(permissions, FINANCE_PERMISSIONS.REVENUE_LEDGER_UNLOCK);
  const canPayOff = hasPermission(permissions, FINANCE_PERMISSIONS.REVENUE_LEDGER_PAY_OFF);

  if (!canRead) {
    return (
      <div className="flex items-center justify-center py-24 text-sm text-muted-foreground">
        您没有查看收益台账的权限。
      </div>
    );
  }

  const { ledgers, loading, error, fetchLedgers, unlockLedger, payOffLedger } = useRevenueLedgers();
  const [filterProjectId, setFilterProjectId] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  useEffect(() => {
    void fetchLedgers({
      orderProjectId: filterProjectId || undefined,
      status: filterStatus || undefined,
    });
  }, [fetchLedgers, filterProjectId, filterStatus]);

  async function handleUnlock(ledger: RevenueLedgerResponseDto) {
    if (!confirm(`解锁收益 ${fmtCny(ledger.netAmount)}？（项目已完成交付）`)) return;
    const result = await unlockLedger(ledger.id, { expectedVersion: ledger.version });
    if (result) {
      void fetchLedgers({ orderProjectId: filterProjectId || undefined, status: filterStatus || undefined });
    }
  }

  async function handlePayOff(ledger: RevenueLedgerResponseDto) {
    if (!confirm(`核销收益 ${fmtCny(ledger.netAmount)}？（财务已实际发放）`)) return;
    const result = await payOffLedger(ledger.id, { expectedVersion: ledger.version });
    if (result) {
      void fetchLedgers({ orderProjectId: filterProjectId || undefined, status: filterStatus || undefined });
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">收益台账</h1>
      </div>

      {/* Filters */}
      <div className="flex gap-3">
        <input
          className="rounded border border-gray-300 px-3 py-1.5 text-sm"
          placeholder="订单项目 ID 过滤"
          value={filterProjectId}
          onChange={(e) => setFilterProjectId(e.target.value)}
        />
        <select
          className="rounded border border-gray-300 px-3 py-1.5 text-sm"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="">全部状态</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
      </div>

      {error && (
        <div className="rounded bg-red-50 px-4 py-3 text-sm text-red-700">{error.message}</div>
      )}

      {loading ? (
        <div className="py-8 text-center text-sm text-muted-foreground">加载中…</div>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-500">ID</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">项目 ID</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">类型</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">毛收入</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">净收入</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">成本</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">状态</th>
                <th className="px-4 py-3 text-right font-medium text-gray-500">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {ledgers.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-8 text-center text-muted-foreground">暂无记录</td>
                </tr>
              ) : (
                ledgers.map((l) => (
                  <tr key={l.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono text-xs text-gray-500">{l.id.slice(0, 8)}…</td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-500">{l.orderProjectId.slice(0, 8)}…</td>
                    <td className="px-4 py-3">{REVENUE_TYPE_LABELS[l.revenueType] ?? l.revenueType}</td>
                    <td className="px-4 py-3">{fmtCny(l.grossAmount)}</td>
                    <td className="px-4 py-3 font-semibold">{fmtCny(l.netAmount)}</td>
                    <td className="px-4 py-3 text-gray-500">{fmtCny(l.costAmount)}</td>
                    <td className="px-4 py-3"><StatusBadge status={l.status} /></td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        {l.status === 'pending_unlock' && canUnlock && (
                          <button
                            onClick={() => { void handleUnlock(l); }}
                            className="rounded bg-blue-600 px-2 py-1 text-xs text-white hover:bg-blue-700"
                          >
                            解锁收益
                          </button>
                        )}
                        {l.status === 'unlocked' && canPayOff && (
                          <button
                            onClick={() => { void handlePayOff(l); }}
                            className="rounded bg-green-600 px-2 py-1 text-xs text-white hover:bg-green-700"
                          >
                            核销发放
                          </button>
                        )}
                        {l.status === 'paid_off' && (
                          <span className="text-xs text-gray-400">已完成</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
